// src/utils/classCodeParser.ts

export interface ClassCodeComponents {
  code: string;
  level: {
    code: string;
    name: string;
    description: string;
    sortOrder: number;
  };
  faculty: {
    code: string;
    name: string;
  };
  subject: {
    code: string;
    name?: string;
  };
  identifier: string;
  sequence: string;
  isFirstClass: boolean; // True if this is the primary class (no sequence letter)
  sequenceNumber: number; // 0 for first class, 1 for A, 2 for B, etc.
}

// Level definitions with hierarchical ordering
const LEVEL_DEFINITIONS: Record<string, { name: string; description: string; sortOrder: number }> = {
  'DI': { name: 'Discover', description: 'Foundation level subjects', sortOrder: 1 },
  'EN': { name: 'Engage', description: 'Intermediate level subjects', sortOrder: 2 },
  'EX': { name: 'Explore', description: 'Intermediate exploration subjects', sortOrder: 2 },
  'FO': { name: 'Focus', description: 'Advanced preparation subjects', sortOrder: 3 },
  'RE': { name: 'Reach', description: 'Extension/enrichment subjects', sortOrder: 4 },
  '12': { name: 'Units 1 & 2', description: 'VCE Units 1 & 2 subjects', sortOrder: 5 },
  '34': { name: 'Units 3 & 4', description: 'VCE Units 3 & 4 subjects', sortOrder: 6 },
  'ORA': { name: 'Oratory', description: 'Pastoral care classes', sortOrder: 0 },
  'EE': { name: 'Engage/Explore', description: 'Combined intermediate subjects', sortOrder: 2 },
  'VMA': { name: 'Vocational Major', description: 'Vocational pathway subjects', sortOrder: 5 },
  'VM': { name: 'Vocational Major', description: 'Vocational pathway variant', sortOrder: 5 }
};

// Faculty definitions
const FACULTY_DEFINITIONS: Record<string, string> = {
  'ART': 'Arts',
  'AGR': 'Agriculture', 
  'ENG': 'English',
  'HUM': 'Humanities',
  'LAN': 'Languages',
  'MAT': 'Mathematics',
  'SCI': 'Science',
  'HPE': 'Health & PE',
  'REL': 'Religion',
  'STE': 'STEAM',
  'VMA': 'Vocational Major',
  'LEN': 'Learning Diversity'
};

// Common subject abbreviations (can be expanded)
const SUBJECT_DEFINITIONS: Record<string, string> = {
  'MAD': 'Media Art & Design',
  'MUS': 'Music',
  'DRA': 'Drama',
  'DAN': 'Dance',
  'ENG': 'English',
  'HUM': 'Humanities',
  'ITA': 'Italian',
  'JAP': 'Japanese',
  'MAT': 'Mathematics',
  'SCI': 'Science',
  'HPE': 'Health & PE',
  'FST': 'Food Studies',
  'ITE': 'Information Technology',
  'TEX': 'Textiles',
  'TIM': 'Timber',
  'REL': 'Religion',
  'PED': 'HPE',
  'BIO': 'Biology',
  'CHE': 'Chemistry',
  'PHY': 'Physics',
  'PSY': 'Psychology',
  'ECO': 'Economics',
  'ACC': 'Accounting',
  'BMA': 'Business Management',
  'LST': 'Legal Studies',
  'HHD': 'Health & Human Development',
  'MME': 'Maths Methods',
  'GMA': 'General Mathematics',
  'FMA': 'Foundation Mathematics',
  'SMA': 'Specialist Mathematics',
  'LIT': 'Literature',
  'MED': 'Media',
  'VCD': 'Visual Communication Design',
  'RSO': 'Religion & Society',
  'AME': 'Art Making & Exhibiting',
  'MRP': 'Music Repertoire & Performance',
  'COM': 'Applied Computing',
  'AHS': 'Agricultural & Horticultural Studies'
};

/**
 * Parse a class code into its components
 */
export function parseClassCode(code: string): ClassCodeComponents {
  const originalCode = code;
  
  // Handle special cases first
  if (code.startsWith('ORA')) {
    return parseOratoryCode(code);
  }
  
  // Standard parsing
  let level = '';
  let faculty = '';
  let subject = '';
  let identifier = '';
  let sequence = '';
  
  // Extract level (first 2-3 characters)
  if (code.match(/^(DI|EN|EX|FO|RE|EE)/)) {
    level = code.substring(0, 2);
    code = code.substring(2);
  } else if (code.match(/^(12|34)/)) {
    level = code.substring(0, 2);
    code = code.substring(2);
  } else if (code.match(/^(VMA|ORA)/)) {
    level = code.substring(0, 3);
    code = code.substring(3);
  } else if (code.match(/^(VM)/)) {
    level = code.substring(0, 2);
    code = code.substring(2);
  }
  
  // Extract faculty (next 3 characters)
  if (code.length >= 3) {
    faculty = code.substring(0, 3);
    code = code.substring(3);
  }
  
  // Extract subject (next 3 characters)
  if (code.length >= 3) {
    subject = code.substring(0, 3);
    code = code.substring(3);
  }
  
  // Extract identifier (next 1 character)
  if (code.length >= 1) {
    identifier = code.substring(0, 1);
    code = code.substring(1);
  }
  
  // Extract sequence (remaining characters)
  sequence = code;
  
  // Determine if this is the first class and sequence number
  const isFirstClass = sequence === '';
  const sequenceNumber = isFirstClass ? 0 : (sequence.charCodeAt(0) - 'A'.charCodeAt(0) + 1);
  
  return {
    code: originalCode,
    level: {
      code: level,
      name: LEVEL_DEFINITIONS[level]?.name || 'Unknown',
      description: LEVEL_DEFINITIONS[level]?.description || 'Unknown level',
      sortOrder: LEVEL_DEFINITIONS[level]?.sortOrder || 999
    },
    faculty: {
      code: faculty,
      name: FACULTY_DEFINITIONS[faculty] || faculty
    },
    subject: {
      code: subject,
      name: SUBJECT_DEFINITIONS[subject]
    },
    identifier,
    sequence,
    isFirstClass,
    sequenceNumber
  };
}

/**
 * Parse Oratory class codes (special format)
 */
function parseOratoryCode(code: string): ClassCodeComponents {
  // Format: ORA + 3-letter house name + year + number
  const match = code.match(/^ORA([A-Z]{3})(\d)(\d)(\d)$/);
  
  if (!match) {
    return {
      code,
      level: { code: 'ORA', name: 'Oratory', description: 'Pastoral care classes', sortOrder: 0 },
      faculty: { code: 'ORA', name: 'Oratory' },
      subject: { code: 'ORA', name: 'Oratory' },
      identifier: '',
      sequence: '',
      isFirstClass: true,
      sequenceNumber: 0
    };
  }
  
  const [, houseName, year, , classNum] = match;
  
  // House name mappings
  const houseNames: Record<string, string> = {
    'BUN': 'Bunjil',
    'COL': 'Colliton', 
    'MAC': 'MacKillop',
    'MAH': 'Mahoney',
    'MAI': 'Maiocco',
    'MAN': 'Mannix',
    'NAU': 'Naughton',
    'OGR': 'O\'Grady'
  };
  
  return {
    code,
    level: { code: 'ORA', name: 'Oratory', description: 'Pastoral care classes', sortOrder: 0 },
    faculty: { code: 'ORA', name: 'Oratory' },
    subject: { 
      code: houseName, 
      name: `${houseNames[houseName] || houseName} - Year ${year}` 
    },
    identifier: year,
    sequence: classNum,
    isFirstClass: classNum === '1',
    sequenceNumber: parseInt(classNum)
  };
}

/**
 * Get all classes with the same base subject (ignoring sequence)
 */
export function getBaseClassCode(code: string): string {
  const parsed = parseClassCode(code);
  return `${parsed.level.code}${parsed.faculty.code}${parsed.subject.code}${parsed.identifier}`;
}

/**
 * Check if two classes are variations of the same subject
 */
export function areRelatedClasses(code1: string, code2: string): boolean {
  return getBaseClassCode(code1) === getBaseClassCode(code2);
}

/**
 * Sort classes by level hierarchy, then by faculty, subject, and sequence
 */
export function sortClassCodes(codes: string[]): string[] {
  return codes.sort((a, b) => {
    const parsedA = parseClassCode(a);
    const parsedB = parseClassCode(b);
    
    // First by level sort order
    if (parsedA.level.sortOrder !== parsedB.level.sortOrder) {
      return parsedA.level.sortOrder - parsedB.level.sortOrder;
    }
    
    // Then by faculty
    if (parsedA.faculty.code !== parsedB.faculty.code) {
      return parsedA.faculty.code.localeCompare(parsedB.faculty.code);
    }
    
    // Then by subject
    if (parsedA.subject.code !== parsedB.subject.code) {
      return parsedA.subject.code.localeCompare(parsedB.subject.code);
    }
    
    // Then by identifier
    if (parsedA.identifier !== parsedB.identifier) {
      return parsedA.identifier.localeCompare(parsedB.identifier);
    }
    
    // Finally by sequence number
    return parsedA.sequenceNumber - parsedB.sequenceNumber;
  });
}

/**
 * Group classes by their base subject
 */
export function groupClassesBySubject(codes: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  
  for (const code of codes) {
    const baseCode = getBaseClassCode(code);
    if (!groups[baseCode]) {
      groups[baseCode] = [];
    }
    groups[baseCode].push(code);
  }
  
  // Sort each group by sequence
  for (const baseCode in groups) {
    groups[baseCode] = sortClassCodes(groups[baseCode]);
  }
  
  return groups;
}

/**
 * Filter classes by level
 */
export function filterClassesByLevel(codes: string[], levelCodes: string[]): string[] {
  return codes.filter(code => {
    const parsed = parseClassCode(code);
    return levelCodes.includes(parsed.level.code);
  });
}

/**
 * Filter classes by faculty
 */
export function filterClassesByFaculty(codes: string[], facultyCodes: string[]): string[] {
  return codes.filter(code => {
    const parsed = parseClassCode(code);
    return facultyCodes.includes(parsed.faculty.code);
  });
}

/**
 * Get a human-readable description of a class code
 */
export function getClassCodeDescription(code: string): string {
  const parsed = parseClassCode(code);
  
  if (parsed.level.code === 'ORA') {
    return `Oratory - ${parsed.subject.name}`;
  }
  
  let description = `${parsed.level.name} ${parsed.faculty.name}`;
  
  if (parsed.subject.name) {
    description += ` - ${parsed.subject.name}`;
  } else if (parsed.subject.code !== parsed.faculty.code) {
    description += ` - ${parsed.subject.code}`;
  }
  
  if (!parsed.isFirstClass) {
    description += ` (Class ${parsed.sequence})`;
  }
  
  return description;
}

/**
 * Extract teaching preferences by analyzing class code patterns
 * This could be used to enhance preference matching
 */
export function analyzeTeachingPatterns(classCodes: string[]): {
  levels: Record<string, number>;
  faculties: Record<string, number>;
  subjects: Record<string, number>;
  sequencePreference: 'primary' | 'varied' | 'multiple';
} {
  const analysis = {
    levels: {} as Record<string, number>,
    faculties: {} as Record<string, number>,
    subjects: {} as Record<string, number>,
    sequencePreference: 'primary' as 'primary' | 'varied' | 'multiple'
  };
  
  let primaryClasses = 0;
  let multipleClasses = 0;
  
  for (const code of classCodes) {
    const parsed = parseClassCode(code);
    
    // Count levels
    analysis.levels[parsed.level.code] = (analysis.levels[parsed.level.code] || 0) + 1;
    
    // Count faculties  
    analysis.faculties[parsed.faculty.code] = (analysis.faculties[parsed.faculty.code] || 0) + 1;
    
    // Count subjects
    const subjectKey = `${parsed.faculty.code}-${parsed.subject.code}`;
    analysis.subjects[subjectKey] = (analysis.subjects[subjectKey] || 0) + 1;
    
    // Track sequence preferences
    if (parsed.isFirstClass) {
      primaryClasses++;
    } else {
      multipleClasses++;
    }
  }
  
  // Determine sequence preference
  if (multipleClasses === 0) {
    analysis.sequencePreference = 'primary';
  } else if (multipleClasses > primaryClasses) {
    analysis.sequencePreference = 'multiple';
  } else {
    analysis.sequencePreference = 'varied';
  }
  
  return analysis;
}